import clsx from 'clsx';
import dayjs from 'dayjs';
import { useUnit } from 'effector-react';
import $$giftTrustPage from 'models/pages/gift-trust';
import { FormProvider, useForm } from 'react-hook-form';

import columns, { type GiftTrustTableItem } from './gift-trust-columns';

import HookFormControl from 'components/HookFormControl';
import CommunityTab from 'components/StyledTemplates/CommunityTab';
import Dropdown from 'components/StyledTemplates/Dropdown';
import Table, { Option } from 'components/Table/Table';

import useResources from 'hooks/useResources';

import imgThreeDotsVertWhite from 'assets/icons/more-vert-white.svg';

import { type QuestionnaireInstance } from 'types/zod/questionnaire-instance';

type InstanceTabProps = {
    qInstanceId: QuestionnaireInstance['questionnaire_instance_id'];
    isFirst?: boolean;
};

const InstanceTab = (props: InstanceTabProps) => {
    const { qInstanceId, isFirst } = props;

    const { t } = useResources();

    const form = useForm();
    const filters = form.watch();

    const { qInstance, giftTrustItems, isSigned, isPayed, name } = useUnit(
        $$giftTrustPage.$tabs.map((tabs) => tabs[qInstanceId]),
    );

    if (!qInstance) {
        return null;
    }

    const status = qInstance.status === 'In_Progress' ? 'In_Progress' : isSigned ? 'Signed' : 'Generated';
    const rows: GiftTrustTableItem[] = giftTrustItems.map((giftTrustItem) => ({ ...giftTrustItem, qInstanceId }));

    return (
        <CommunityTab
            closable={true}
            isOpenedDefault={isFirst}
            title={name || t('giftTrustPage.giftTrustNamePlaceholder')}
            rightEndComponent={
                <div className="d-flex flex-column flex-sm-row align-items-start align-items-md-center justify-content-end gap-2 gap-sm-5">
                    <div className="d-flex align-items-center justify-content-center">
                        <strong className="text-white">{t('giftTrustPage.date')}</strong>
                        <span className="text-white ml-2">{dayjs(qInstance.date_added).format('MM/DD/YYYY')}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 w-100 justify-content-between">
                        <strong
                            className={clsx(
                                'd-flex align-items-center justify-content-center rounded-pill px-3 py-1 status',
                                {
                                    'bg-permission': status === 'In_Progress',
                                    'bg-laurel': status === 'Generated',
                                    'bg-primary': status === 'Signed',
                                },
                            )}
                        >
                            {t(`giftTrustPage.giftTrustStatuses.${status}`)}
                        </strong>
                        <Dropdown
                            toggle={<img alt="Three dots" src={imgThreeDotsVertWhite} />}
                            menu={
                                {
                                    In_Progress: (
                                        <>
                                            <Option onClick={() => $$giftTrustPage.resumeButtonClicked(qInstanceId)}>
                                                {t('giftTrustPage.instanceOptions.resume')}
                                            </Option>
                                            {!isPayed && (
                                                <Option
                                                    onClick={() => $$giftTrustPage.deleteButtonClicked(qInstanceId)}
                                                >
                                                    {t('giftTrustPage.instanceOptions.delete')}
                                                </Option>
                                            )}
                                        </>
                                    ),
                                    Signed: (
                                        <>
                                            <Option onClick={() => $$giftTrustPage.documentsButtonClicked(qInstanceId)}>
                                                {t('giftTrustPage.instanceOptions.giftTrustDocuments')}
                                            </Option>
                                        </>
                                    ),
                                    Generated: (
                                        <>
                                            <Option onClick={() => $$giftTrustPage.documentsButtonClicked(qInstanceId)}>
                                                {t('giftTrustPage.instanceOptions.giftTrustDocuments')}
                                            </Option>
                                            <Option onClick={() => $$giftTrustPage.updateButtonClicked(qInstanceId)}>
                                                {t('giftTrustPage.instanceOptions.update')}
                                            </Option>
                                        </>
                                    ),
                                }[status]
                            }
                        />
                    </div>
                </div>
            }
            children={
                status === 'Signed' && rows.length > 0 ? (
                    <>
                        <FormProvider {...form}>
                            <div className="row w-100 justify-content-between">
                                <HookFormControl.Text
                                    name="description"
                                    className="form-control col-4"
                                    placeholder={t('giftTrustPage.table.filters.input.placeholder')}
                                />
                                <button
                                    className="btn btn-outline-dark col-2"
                                    type="button"
                                    onClick={() => $$giftTrustPage.addGiftButtonClicked(qInstanceId)}
                                >
                                    {t('giftTrustPage.table.filters.button.addGift')}
                                </button>
                            </div>
                        </FormProvider>
                        <Table
                            columns={columns(t)}
                            rows={rows}
                            idColumnName="qInstanceId"
                            name={`GIFT_TRUST_${qInstanceId}`}
                            filters={filters}
                            withPagination={false}
                            extra={
                                '@media (max-width: 768px) { table { max-width: 100vw; overflow-x: scroll; }; tr { width: 700px; } } }'
                            }
                        />
                    </>
                ) : (
                    <>
                        <div className="row w-100 justify-content-between">
                            <p className="col-9">{t(`giftTrustPage.table.description.${status}`)}</p>
                            {status === 'In_Progress' && (
                                <button
                                    className="btn btn-outline-dark col-2"
                                    type="button"
                                    onClick={() => $$giftTrustPage.resumeButtonClicked(qInstanceId)}
                                >
                                    {t('giftTrustPage.table.filters.button.continue')}
                                </button>
                            )}
                            {status === 'Generated' && (
                                <button
                                    className="btn btn-outline-dark col-2"
                                    type="button"
                                    onClick={() => $$giftTrustPage.markAsSignedClicked(qInstanceId)}
                                >
                                    {t('giftTrustPage.table.filters.button.markAsSigned')}
                                </button>
                            )}
                            {status === 'Signed' && (
                                <button
                                    className="btn btn-outline-dark col-2"
                                    type="button"
                                    onClick={() => $$giftTrustPage.addGiftButtonClicked(qInstanceId)}
                                >
                                    {t('giftTrustPage.table.filters.button.addGift')}
                                </button>
                            )}
                        </div>
                    </>
                )
            }
        />
    );
};

export default InstanceTab;
